import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CheckCircle2, Loader2, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthShell } from "@/components/AuthShell";
import { BackToLogin } from "@/pages/ForgotPassword";
import api, { formatApiError } from "@/lib/api";

function StatusCard({ icon: Icon, tone, title, body, testId, children }) {
  return (
    <div data-testid={testId}>
      <div className={`h-12 w-12 rounded-2xl flex items-center justify-center ${tone}`}>
        <Icon className="h-5 w-5" />
      </div>
      <h1 className="mt-6 text-3xl font-semibold tracking-tight">{title}</h1>
      <p className="mt-3 text-muted-foreground leading-relaxed">{body}</p>
      {children}
    </div>
  );
}

function PasswordField({ id, label, value, onChange, testId }) {
  return (
    <div>
      <Label htmlFor={id} className="text-xs font-medium">
        {label}
      </Label>
      <Input
        id={id}
        type="password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        minLength={8}
        autoComplete="new-password"
        className="mt-1.5 h-11 rounded-xl"
        placeholder="••••••••"
        data-testid={testId}
      />
    </div>
  );
}

export default function ResetPassword() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState("checking"); // checking | form | invalid | done
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) return setState("invalid");
    api
      .post("/auth/reset-password/check", { token })
      .then((r) => setState(r.data?.valid ? "form" : "invalid"))
      .catch(() => setState("invalid"));
  }, [token]);

  async function submit(e) {
    e.preventDefault();
    if (pwd !== confirm) return toast.error(t("auth.password_mismatch"));
    setSaving(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setState("done");
    } catch (err) {
      if (err?.response?.data?.detail?.code === "reset_link_invalid") setState("invalid");
      else toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <AuthShell>
      {state === "checking" && (
        <div className="flex items-center gap-2 text-muted-foreground" data-testid="reset-checking">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t("auth.checking")}
        </div>
      )}
      {state === "invalid" && (
        <StatusCard
          icon={ShieldAlert}
          tone="bg-amber-500/15 text-amber-600 dark:text-amber-400"
          title={t("auth.reset_invalid_title")}
          body={t("auth.reset_invalid_body")}
          testId="reset-invalid"
        >
          <Button asChild className="mt-6 h-11 rounded-xl bg-foreground text-background hover:bg-foreground/90">
            <Link to="/forgot-password" data-testid="reset-request-new-link">
              {t("auth.request_new_link")}
            </Link>
          </Button>
          <div>
            <BackToLogin />
          </div>
        </StatusCard>
      )}
      {state === "done" && (
        <StatusCard
          icon={CheckCircle2}
          tone="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
          title={t("auth.reset_done_title")}
          body={t("auth.reset_done_body")}
          testId="reset-done"
        >
          <Button asChild className="mt-6 h-11 rounded-xl bg-foreground text-background hover:bg-foreground/90">
            <Link to="/login" data-testid="reset-go-login-btn">
              {t("auth.go_to_login")}
            </Link>
          </Button>
        </StatusCard>
      )}
      {state === "form" && (
        <form onSubmit={submit} data-testid="reset-form">
          <h1 className="text-3xl font-semibold tracking-tight">{t("auth.reset_title")}</h1>
          <p className="mt-2 text-muted-foreground">{t("auth.reset_subtitle")}</p>
          <div className="mt-8 space-y-4">
            <PasswordField id="new-password" label={t("auth.new_password")} value={pwd} onChange={setPwd} testId="reset-password-input" />
            <PasswordField id="confirm-password" label={t("auth.confirm_password")} value={confirm} onChange={setConfirm} testId="reset-confirm-input" />
          </div>
          <Button
            type="submit"
            disabled={saving}
            className="mt-6 w-full h-11 rounded-xl bg-foreground text-background hover:bg-foreground/90"
            data-testid="reset-submit-btn"
          >
            {saving ? t("auth.reset_saving") : t("auth.reset_submit")}
          </Button>
          <BackToLogin />
        </form>
      )}
    </AuthShell>
  );
}
